import {
  Button,
  Flex,
  Form,
  Switch,
  Tag,
  theme,
  Tooltip,
  Typography
} from 'antd'
import { AlertTriangle, Info, Settings, Users as UsersIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  OrganizationAuthMethodEnum,
  type OrganizationAuthMethod
} from 'src/api/main'
import { resolveIconColors } from 'src/components'

import { ProviderSetupDrawer } from './ProviderSetupDrawer'
import { useStyles } from './styles'
import type { BuiltInProviderDescriptor } from './types'

const { Text } = Typography

type Props = {
  descriptor: BuiltInProviderDescriptor
  /** Saved auth method (if any). Used to read masked-secret + user count. */
  saved?: OrganizationAuthMethod
  userCount?: number
  isLast?: boolean
  onSave?: () => void
  saving?: boolean
}

const isApple = (m: BuiltInProviderDescriptor['method']) =>
  m === OrganizationAuthMethodEnum.APPLE
const isEntra = (m: BuiltInProviderDescriptor['method']) =>
  m === OrganizationAuthMethodEnum.ENTRAID

export const BuiltInProviderRow = ({
  descriptor,
  saved,
  userCount,
  isLast,
  onSave,
  saving
}: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const tile = resolveIconColors(token, descriptor.iconColor)
  const { styles, cx } = useStyles()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const enabledFieldName = `${descriptor.key}_enabled`

  // Credential fields (client_id/tenant_id/key_id) live in Form.Items
  // inside the drawer; while the drawer is closed they're unmounted and
  // Form.useWatch on them returns undefined even after setFieldsValue,
  // which falsely raised `Needs setup` for fully-configured providers.
  // The badge reflects persisted state — read it from `saved`.
  const missingClientId = descriptor.hasCredentials && !saved?.client_id
  const missingTenantId =
    (isApple(descriptor.method) || isEntra(descriptor.method)) &&
    !saved?.tenant_id
  const missingKeyId = isApple(descriptor.method) && !saved?.key_id

  const needsSetup =
    descriptor.hasCredentials &&
    saved?.enabled === true &&
    (missingClientId || missingTenantId || missingKeyId)

  const needsApi = descriptor.needsApi === true

  // The setup icon is the entry point for every provider: configurables get
  // the gear, info-only providers (password, passkey) get an info glyph.
  const SetupIcon = descriptor.hasCredentials || needsApi ? Settings : Info
  const setupTooltipKey = descriptor.hasCredentials
    ? 'workspace.authentication.actions.setup'
    : 'workspace.authentication.actions.viewInfo'

  return (
    <>
      <div className={cx(styles.providerRow, isLast && styles.providerRowLast)}>
        <Flex align="center" justify="space-between" gap={12}>
          <Flex align="center" gap={11} style={{ minWidth: 0, flex: 1 }}>
            <span
              className={styles.providerIcon}
              style={{
                background: tile.backgroundColor,
                color: tile.iconColor
              }}
            >
              {descriptor.renderIcon(16)}
            </span>
            <Flex vertical style={{ minWidth: 0, lineHeight: 1.3 }}>
              <Flex align="center" gap={6} wrap="wrap">
                <Text strong style={{ fontSize: 13 }}>
                  {t(descriptor.i18nKey)}
                </Text>
                <Tag className={styles.protocolTag}>{descriptor.protocol}</Tag>
                {needsApi ? (
                  <Tooltip
                    title={t(
                      'workspace.authentication.providers.apiPendingHint'
                    )}
                  >
                    <Tag className={styles.attentionTag}>
                      <AlertTriangle size={10} style={{ marginRight: 4 }} />
                      {t('workspace.authentication.providers.apiPendingLabel')}
                    </Tag>
                  </Tooltip>
                ) : null}
                {needsSetup ? (
                  <Tooltip
                    title={t(
                      'workspace.authentication.providers.needsSetupHint'
                    )}
                  >
                    <Tag className={styles.attentionTag}>
                      <AlertTriangle size={10} style={{ marginRight: 4 }} />
                      {t('workspace.authentication.providers.needsSetupLabel')}
                    </Tag>
                  </Tooltip>
                ) : null}
              </Flex>
              <Text
                type="secondary"
                style={{ fontSize: 11 }}
                ellipsis={{
                  tooltip: t(`${descriptor.i18nKey}Subtitle`, {
                    defaultValue: ''
                  })
                }}
              >
                {t(`${descriptor.i18nKey}Subtitle`, { defaultValue: '' })}
              </Text>
            </Flex>
          </Flex>
          <Flex align="center" gap={12}>
            {typeof userCount === 'number' && userCount > 0 ? (
              <Tooltip
                title={t('workspace.authentication.activeUsersTooltip', {
                  count: userCount
                })}
              >
                <Flex align="center" gap={4}>
                  <UsersIcon size={12} />
                  <Text
                    strong
                    style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {userCount}
                  </Text>
                </Flex>
              </Tooltip>
            ) : null}
            <Tooltip title={t(setupTooltipKey)}>
              <Button
                size="small"
                type="text"
                icon={<SetupIcon size={14} />}
                onClick={() => setDrawerOpen(true)}
                aria-label={t(setupTooltipKey)}
              />
            </Tooltip>
            <Form.Item name={enabledFieldName} valuePropName="checked" noStyle>
              <Switch size="small" disabled={needsApi} />
            </Form.Item>
          </Flex>
        </Flex>
      </div>

      <ProviderSetupDrawer
        open={drawerOpen}
        descriptor={descriptor}
        saved={saved}
        onClose={() => setDrawerOpen(false)}
        onSave={
          onSave
            ? () => {
                onSave()
                setDrawerOpen(false)
              }
            : undefined
        }
        saving={saving}
      />
    </>
  )
}
